import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { ArrowRight, Check } from "lucide-react";

export default function Pitch() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 480, height: 480, background: "#15FF73", top: -120, right: -100 }} />
      <div className="blob" style={{ width: 380, height: 380, background: "#FF1A8C", bottom: -180, left: -120, animationDelay: "-6s" }} />
      <NavBar />
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-14 relative">
        <span className="sticker sticker-magenta mb-4 inline-block">For shop owners</span>
        <h1 className="font-display text-6xl sm:text-7xl lg:text-[112px] font-extrabold leading-[0.88] tracking-[-0.04em] mb-7 mt-3">
          Pay only when <span className="text-rainbow">it works.</span>
        </h1>
        <p className="text-xl text-[#0B0F0C]/70 max-w-2xl mb-10 leading-relaxed">
          Closed-loop. Prepaid wallet. Auto-pause at $0. QR scan = verified visit = commission charged.<br />
          <span className="font-script text-3xl text-[#FF1A8C]">— that simple.</span>
        </p>
        <div className="flex gap-3 mb-16 flex-wrap">
          <Link to="/auth?mode=register&role=dispensary" className="btn-x btn-primary">List my shop <ArrowRight size={16} /></Link>
          <Link to="/billing" className="btn-x btn-ghost">See pricing →</Link>
        </div>

        {/* 3 BLOCKS */}
        <div className="grid md:grid-cols-3 gap-5 mb-16">
          <Block emoji="🎟️" kicker="Acquire" title="QR codes per claim" body="Customer claims → unique code. They walk in. You scan. Done." />
          <Block emoji="💸" kicker="Activate" title="First-visit deals" body="Exclusive 20-30% offers. Paid only on conversion. No commitment." />
          <Block emoji="🏆" kicker="Retain" title="XP keeps 'em loyal" body="Returning customers see your shop ranked higher. Streaks pay." />
        </div>

        <h2 className="font-display text-4xl sm:text-5xl font-extrabold mb-2 tracking-tight">Lean pricing</h2>
        <p className="text-[#0B0F0C]/55 mb-7">Less is more. Pay for what works.</p>
        <div className="space-y-3 mb-10">
          <Tier name="Pay-per-conversion" price="$3–5 / verified visit" tone="card-green"
            details={["QR scan or in-app referral", "Charged only on real check-in", "Avg basket $65–$110 → 4-7% CAC"]} />
          <Tier name="Pro subscription" price="$49 / month" highlight tone="card-magenta"
            details={["Unlimited deals & products", "Full analytics + commission tracking", "Inclusion in Bud's context", "Priority support"]} />
          <Tier name="Featured listing" price="$29 / month" tone="card-cyan"
            details={["Pinned on map + city + category", "Priority in Bud's picks", "2 sponsored flash deals included"]} />
          <Tier name="Sponsored flash deal" price="$9 per 48h boost" tone="card-sunset"
            details={["Top of Deals page", "Push to opted-in users in radius", "Avg 3.4× SKU lift in window"]} />
        </div>
        <div className="flex gap-3 mb-16 flex-wrap">
          <Link to="/billing" className="btn-x btn-primary">Buy a tier <ArrowRight size={16} /></Link>
        </div>

        <h2 className="font-display text-4xl sm:text-5xl font-extrabold mb-2 tracking-tight">Live now</h2>
        <p className="text-[#0B0F0C]/55 mb-7">Everything below is shipped, today.</p>
        <div className="grid md:grid-cols-2 gap-5 mb-16">
          <Phase title="Consumer + shop loop" tone="card-green" items={[
            "QR claim + redemption + commission",
            "Prepaid wallet + auto-pause",
            "Best Value Score on every product",
            "Flash deals + countdowns",
            "Camera QR scan (Scanner page)",
            "Voice search in Bud chat",
          ]} />
          <Phase title="Growth + intelligence" tone="card-magenta" highlight items={[
            "Bud (Claude / GPT / Gemini)",
            "Bud Vision (image strain analysis)",
            "GAS / PASS reviews + smoke tests",
            "Gamified XP + streaks + levels",
            "Stripe live billing (test mode)",
            "Brevo transactional emails",
            "Real interactive map",
          ]} />
        </div>

        <div className="card-x card-green bg-[#FAF6E8] p-10">
          <h2 className="font-display text-4xl font-extrabold mb-3">Ready?</h2>
          <p className="text-[#0B0F0C]/65 mb-6 max-w-xl">List free. We don't bill until your first tracked conversion clears.</p>
          <Link to="/auth?mode=register&role=dispensary" className="btn-x btn-primary">List my shop <ArrowRight size={16} /></Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Block({ emoji, kicker, title, body }) {
  return (
    <div className="card-x card-cyan bg-[#FAF6E8] p-6">
      <div className="text-5xl mb-3 bouncy">{emoji}</div>
      <div className="label-overline text-[#FF1A8C] mb-2">{kicker}</div>
      <h3 className="font-display text-2xl font-extrabold mb-2 leading-tight">{title}</h3>
      <p className="text-sm text-[#0B0F0C]/65 leading-relaxed">{body}</p>
    </div>
  );
}
function Tier({ name, price, details, highlight, tone }) {
  return (
    <div className={`card-x ${tone} bg-[#FAF6E8] p-6 grid md:grid-cols-3 gap-5`}>
      <div>
        <div className="label-overline text-[#FF1A8C] mb-1">{highlight ? "Recommended" : "Tier"}</div>
        <h3 className="font-display text-2xl font-extrabold">{name}</h3>
        <div className="font-mono-x font-bold mt-2">{price}</div>
      </div>
      <ul className="md:col-span-2 space-y-2 text-sm">
        {details.map((d) => <li key={d} className="flex gap-2"><Check size={14} strokeWidth={2.4} className="text-[#15FF73] flex-shrink-0 mt-0.5" /><span>{d}</span></li>)}
      </ul>
    </div>
  );
}
function Phase({ title, items, highlight, tone }) {
  return (
    <div className={`card-x ${tone} bg-[#FAF6E8] p-6`}>
      <h3 className="font-display text-2xl font-extrabold mb-4">{title}</h3>
      <ul className="space-y-2 text-sm">
        {items.map((i) => <li key={i} className="flex gap-2"><Check size={14} strokeWidth={2.4} className={highlight ? "text-[#FF1A8C]" : "text-[#15FF73]"} /> <span>{i}</span></li>)}
      </ul>
    </div>
  );
}
